import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { apiClient } from '../../services/api';
import { FiSearch, FiZap, FiMapPin, FiGlobe, FiTool, FiArrowRight, FiCheck } from 'react-icons/fi';
import toast from 'react-hot-toast';

interface Props {
    onComplete?: (realms: any[]) => void;
}

const SkillDiscoveryWizard: React.FC<Props> = ({ onComplete }) => {
    const [step, setStep] = useState(1);
    const [inputSkills, setInputSkills] = useState('');
    const [analysis, setAnalysis] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedRealms, setSelectedRealms] = useState<string[]>([]);

    const handleAnalyze = async () => {
        if (!inputSkills.trim()) return;
        setLoading(true);
        try {
            const data = await apiClient.analyzeSkills(inputSkills);
            setAnalysis(data.analysis || []);
            setStep(2);
        } catch (err) {
            console.error('Skill analysis failed:', err);
            toast.error('Failed to map skills. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const toggleRealm = (realmCode: string) => {
        setSelectedRealms(prev =>
            prev.includes(realmCode) ? prev.filter(r => r !== realmCode) : [...prev, realmCode]
        );
    };

    const handleApply = () => {
        const chosen = analysis.filter(a => selectedRealms.includes(a.realmCode));
        toast.success(`${chosen.length} professional realms added to your profile roadmap.`);
        if (onComplete) onComplete(chosen);
    };

    return (
        <div className="bg-black/40 backdrop-blur-xl border border-premium-gold/30 rounded-3xl overflow-hidden shadow-2xl max-w-2xl mx-auto">
            <div className="p-8 border-b border-premium-gold/10 bg-gradient-to-r from-premium-gold/5 to-transparent">
                <h2 className="text-2xl font-serif font-bold text-white mb-2 flex items-center gap-3">
                    <span className="text-premium-gold">🧠</span> Skill Discovery Engine
                </h2>
                <p className="text-white/40 text-sm">Mapping your hands-on skills to global opportunity pathways.</p>
            </div>

            <div className="p-8 min-h-[300px] flex flex-col justify-center">
                <AnimatePresence mode="wait">
                    {step === 1 && (
                        <motion.div
                            key="step1"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className="space-y-6"
                        >
                            <div className="text-center">
                                <h3 className="text-xl text-white mb-6">What can you do?</h3>
                                <p className="text-white/60 text-sm mb-8 italic">"I can fix phones, farm maize, weld metal, and drive heavy trucks..."</p>

                                <textarea
                                    value={inputSkills}
                                    onChange={(e) => setInputSkills(e.target.value)}
                                    placeholder="Type your skills here (comma separated or just a sentence)..."
                                    className="w-full bg-white/5 border border-white/10 rounded-2xl p-6 text-white placeholder-white/20 focus:outline-none focus:border-premium-gold/50 transition-all min-h-[120px]"
                                />
                            </div>

                            <button
                                onClick={handleAnalyze}
                                disabled={loading || !inputSkills.trim()}
                                className="w-full py-4 bg-premium-gold text-premium-wood-dark font-bold rounded-2xl flex items-center justify-center gap-2 hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all disabled:opacity-50"
                            >
                                {loading ? <div className="w-5 h-5 border-2 border-premium-wood-dark border-t-transparent animate-spin rounded-full" /> : <><FiZap /> Map My Skills</>}
                            </button>
                        </motion.div>
                    )}

                    {step === 2 && (
                        <motion.div
                            key="step2"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className="space-y-6"
                        >
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-lg font-bold text-white">Discovery Results</h3>
                                <button onClick={() => setStep(1)} className="text-xs text-premium-gold/60 hover:text-premium-gold transition-colors underline">Start Over</button>
                            </div>

                            <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2 scrollbar-premium">
                                {analysis.map((item, idx) => (
                                    <motion.div
                                        key={idx}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: idx * 0.1 }}
                                        onClick={() => toggleRealm(item.realmCode)}
                                        className={`p-6 rounded-2xl border cursor-pointer transition-all ${selectedRealms.includes(item.realmCode)
                                                ? 'bg-premium-gold/10 border-premium-gold/40 shadow-[0_0_15px_rgba(212,175,55,0.1)]'
                                                : 'bg-white/5 border-white/10 hover:border-white/20'
                                            }`}
                                    >
                                        <div className="flex justify-between items-start mb-4">
                                            <div>
                                                <div className="text-premium-gold text-xs font-bold uppercase tracking-widest mb-1">{item.realmCode}</div>
                                                <h4 className="text-lg font-serif font-bold text-white">{item.realmName}</h4>
                                            </div>
                                            <div className="text-[10px] bg-premium-gold/20 text-premium-gold px-2 py-1 rounded">
                                                {item.confidence}% Match
                                            </div>
                                        </div>

                                        <p className="text-xs text-white/60 mb-4 line-clamp-2">{item.description}</p>

                                        <div className="flex flex-wrap gap-2 mb-4">
                                            {item.matchedSkills.slice(0, 3).map((s: string, i: number) => (
                                                <span key={i} className="px-2 py-1 bg-white/5 rounded text-[10px] text-white/40 flex items-center gap-1">
                                                    <FiCheck className="text-green-500" /> {s}
                                                </span>
                                            ))}
                                        </div>

                                        <div className="pt-4 border-t border-white/10 flex flex-wrap gap-4">
                                            {item.opportunityPathways.map((p: string, i: number) => (
                                                <div key={i} className="flex items-center gap-1 text-[10px] font-bold text-premium-gold/80">
                                                    {p.includes('Remote') ? <FiGlobe /> : p.includes('Local') ? <FiMapPin /> : <FiTool />}
                                                    {p}
                                                </div>
                                            ))}
                                        </div>
                                    </motion.div>
                                ))}

                                {analysis.length === 0 && (
                                    <div className="text-center py-12">
                                        <p className="text-white/40">We couldn't map these skills to a specific realm yet.</p>
                                    </div>
                                )}
                            </div>

                            <button
                                onClick={handleApply}
                                disabled={selectedRealms.length === 0}
                                className="w-full py-4 bg-premium-gold text-premium-wood-dark font-bold rounded-2xl flex items-center justify-center gap-2 hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all disabled:opacity-50"
                            >
                                Apply to My Passport <FiArrowRight />
                            </button>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};

export default SkillDiscoveryWizard;
